/**
 * Hybrid interpolator — a port of hybridInterp.m.
 *
 * Two stages, and on integer rate ratios only the first one runs:
 *
 *   1. Integer upsample by `L = ceil(contextRate / fileRate)`: zero-stuff, then
 *      filter with a Hamming-windowed linear-phase FIR lowpass cutting at the
 *      input's Nyquist. This is the anti-imaging filter and it does the real
 *      work.
 *   2. PCHIP cubic between the upsampled samples, for whatever fraction is left
 *      over. On 8k/16k/24k into a 48 kHz context there is no remainder — the
 *      MATLAB skips this branch entirely — so it only engages on ratios like
 *      44.1→48 kHz, or when playing at something other than 1x.
 *
 * PCHIP is Fritsch–Carlson shape-preserving Hermite, not Catmull-Rom: the
 * tangent at each point is a weighted harmonic mean of the neighbouring slopes,
 * forced to zero at a local extremum. Same four-point footprint as
 * `hermite.js`, but it cannot overshoot a step.
 *
 * ── Deliberate differences from the MATLAB ──
 *
 * - **Filter order 254, not 255.** `fir1(255, ·)` is 256 taps and so has a
 *   127.5-sample group delay, which the MATLAB leaves uncompensated. Here the
 *   delay is compensated so the output stays aligned with the source — a DAW
 *   cannot afford a 2.66 ms shift on one track — and an integer delay is what
 *   makes that exact. One tap's difference in the response.
 * - **Gain compensated by L.** Zero-stuffing by L and filtering with a
 *   unity-DC-gain filter leaves the output at 1/L amplitude; the MATLAB doesn't
 *   correct for it, so its output is 6x quiet on an 8 kHz file. Comparing
 *   kernels at different levels would be meaningless.
 * - **Polyphase rather than zero-stuff-then-convolve.** Identical arithmetic
 *   with the multiplications by zero removed, evaluated at an arbitrary
 *   position so it works in a streaming pump rather than over a whole signal.
 *   At an integer ratio it reduces to a single branch and reproduces the
 *   MATLAB's integer path.
 */

/** Filter order. See the note above on why this is 254 and not the MATLAB's 255. */
const HYBRID_FIR_ORDER = 254;

/**
 * MATLAB `fir1(order, cutoff)`: window-method lowpass, Hamming window,
 * normalised to unity gain at DC.
 *
 * `cutoff` is relative to Nyquist, matching MATLAB's convention.
 */
export function fir1Lowpass(order, cutoff) {
  const length = order + 1;
  const centre = order / 2;
  const h = new Float64Array(length);
  let sum = 0;

  for (let k = 0; k < length; k++) {
    const x = k - centre;
    // Ideal lowpass: cutoff * sinc(cutoff * x), with sinc(z) = sin(pi z)/(pi z).
    const ideal = x === 0 ? cutoff : Math.sin(Math.PI * cutoff * x) / (Math.PI * x);
    const hamming = 0.54 - 0.46 * Math.cos((2 * Math.PI * k) / order);

    h[k] = ideal * hamming;
    sum += h[k];
  }

  // fir1's default 'scale': make the passband exactly unity at DC.
  for (let k = 0; k < length; k++) h[k] /= sum;
  return h;
}

/**
 * Split the FIR into polyphase branches, scaled by L to undo the amplitude loss
 * that zero-stuffing causes.
 *
 * Branch `r` holds b[r], b[r+L], b[r+2L], … — the taps that a zero-stuffed
 * signal would actually land on when the upsampled index is congruent to r.
 */
export function polyphaseBranches(coefficients, upFactor) {
  const branchLength = Math.ceil(coefficients.length / upFactor);
  const table = new Float32Array(upFactor * branchLength);

  for (let r = 0; r < upFactor; r++) {
    for (let s = 0; s < branchLength; s++) {
      const tap = r + s * upFactor;
      table[r * branchLength + s] = tap < coefficients.length ? coefficients[tap] * upFactor : 0;
    }
  }

  return { table, branchLength };
}

export function readHybrid(w, i, mu, table, params) {
  const upFactor = params.upFactor;
  const branchLength = params.branchLength;

  // Position on the upsampled grid, advanced past the filter's group delay.
  const u = mu * upFactor + params.delay;
  const j = Math.floor(u);
  const t = u - j;

  // One polyphase branch = the upsampled signal at index (base * L + m).
  const upsampled = (m) => {
    let phase = m % upFactor;
    if (phase < 0) phase += upFactor;

    const offset = i + Math.floor(m / upFactor);
    const row = phase * branchLength;

    let acc = 0;
    for (let s = 0; s < branchLength; s++) acc += table[row + s] * w[offset - s];
    return acc;
  };

  const y1 = upsampled(j);

  // Integer ratio: the position lands exactly on an upsampled sample and there
  // is nothing for the cubic to do. This is the MATLAB's `srInt == srOut` path,
  // and it's every one of af's rates into a 48 kHz context.
  if (t === 0) return y1;

  const y0 = upsampled(j - 1);
  const y2 = upsampled(j + 1);
  const y3 = upsampled(j + 2);

  // PCHIP tangents (Fritsch-Carlson) on a uniform grid: the weighted harmonic
  // mean of the two adjacent slopes, and zero wherever they disagree in sign so
  // the curve can't overshoot through a local extremum.
  const slope0 = y1 - y0;
  const slope1 = y2 - y1;
  const slope2 = y3 - y2;

  const m1 = slope0 * slope1 <= 0 ? 0 : (2 * slope0 * slope1) / (slope0 + slope1);
  const m2 = slope1 * slope2 <= 0 ? 0 : (2 * slope1 * slope2) / (slope1 + slope2);

  const c3 = m1 + m2 - 2 * slope1;
  const c2 = 3 * slope1 - 2 * m1 - m2;
  return ((c3 * t + c2) * t + m1) * t + y1;
}

export function createHybrid({ order = HYBRID_FIR_ORDER } = {}) {
  return {
    id: "hybrid",
    label: "Hybrid FIR + PCHIP (MATLAB port)",
    kernel: "hybrid",
    needsRates: true,
    // Not an interpolator: it applies an anti-imaging filter at every position,
    // including exact sample hits. The pump must not skip it.
    identityAtZero: false,
    notes: `${order + 1}-tap anti-imaging FIR then PCHIP cubic. Port of hybridInterp.m.`,

    /**
     * The filter depends on the rate ratio — exactly as the MATLAB's does,
     * where `intFactor` comes from srIn and srOut — so it can't be built until
     * a track's rates are known.
     */
    configure({ fileRate, contextRate }) {
      const upFactor = Math.max(1, Math.ceil(contextRate / fileRate));

      // L = 1 means no upsampling to do, and fir1's cutoff would sit at Nyquist,
      // which MATLAB rejects outright. Fall back to the cubic alone.
      if (upFactor === 1) {
        return {
          table: new Float32Array([1]),
          params: { upFactor: 1, branchLength: 1, delay: 0 },
          before: 1,
          after: 2,
          taps: 4,
        };
      }

      const coefficients = fir1Lowpass(order, 1 / upFactor);
      const { table, branchLength } = polyphaseBranches(coefficients, upFactor);
      const delay = order / 2; // integer, because the order is even

      // The window has to cover every input sample the four polyphase
      // evaluations can reach.
      const lowestIndex = Math.floor((delay - 1) / upFactor);
      const highestIndex = Math.floor((delay + upFactor + 1) / upFactor);

      const before = branchLength - 1 - lowestIndex;
      const after = highestIndex;

      return {
        table,
        params: { upFactor, branchLength, delay },
        before,
        after,
        taps: before + after + 1,
      };
    },
  };
}
